import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild, SimpleChanges } from '@angular/core';
import { UpdoxDirectMessage } from '../updox-direct-message';
import { UpdoxMessagesService } from '../updox-messages.service';
import { MessageAttachment, PendingAttachment, DocumentType } from '../updox-attachment';
import { AlertService } from '../../../../_alert';
import { GlobalErrorHandlerService } from '../../../../services/global-error.service';

const MAX_POLL_ATTEMPTS = 12; // 12 × 4 s = 48 s maximum wait
// Caps how long message-detail's "Checking for a linked patient…" state
// waits on the C-CDA hint lookup before settling — a slow parse must not
// leave the top of the message looking stuck.
const CCDA_HINT_TIMEOUT_MS = 3500;

@Component({
  selector: 'app-updox-attachments',
  templateUrl: './attachments.component.html',
})
export class UpdoxAttachmentsComponent implements OnChanges, OnDestroy {
  @Input() message: UpdoxDirectMessage;
  // Sent-folder attachments are view-only — the practice sent them, so add-to-chart
  // and clinical-summary ingestion don't apply.
  @Input() readOnly = false;

  // Fires once the background C-CDA patient check is settled — with the
  // best-guess chart match (if any) and the document's raw parsed
  // demographics (if the C-CDA parsed at all, regardless of whether it
  // matched anyone — lets the parent prefill "Add New Patient" on a miss).
  // Always fires exactly once per message (when relevant) specifically so
  // the parent (message-detail) knows when it's safe to stop showing a
  // "Checking for a linked patient…" state instead of only firing on a hit
  // and leaving the parent's loading state hanging on a miss.
  @Output() ccdaPatientDetected = new EventEmitter<{ matched: any; docPatient: any }>();

  attachments: MessageAttachment[] = [];
  pending: PendingAttachment[] = [];
  hasCcda = false;
  isLoading = false;
  pollTimedOut = false;

  // ── Add-to-chart form ──────────────────────────────────────────────────────
  // Patient selection itself no longer happens here — message.patient_id is
  // the one source of truth, resolved and authorized at the top of the
  // message (message-detail's sticky patient bar) before staff can even open
  // this form (see the disabled state in the template). This form just files
  // the document under whichever patient is already linked.
  activeAddToChartId: number = null;
  isAddingToChart = false;
  addToChartDocTypeId: number = null;
  addToChartName = '';
  addToChartDate = '';
  documentTypes: DocumentType[] = [];
  documentTypesLoaded = false;

  // ── Remove-from-chart confirm ────────────────────────────────────────────────
  activeRemoveFromChartId: number = null;
  isRemovingFromChart = false;

  // ── C-CDA preview ──────────────────────────────────────────────────────────
  activeCcdaAttachment: MessageAttachment = null;
  activeCcdaPendingIndex: number = null;

  private pollTimer: any;
  private pollCount = 0;
  // Guards the hint lookup to at most once per message — loadAttachments()
  // itself polls every 4s while attachments are still processing, and this
  // must not re-fire (or re-prompt the user) on every one of those polls.
  private ccdaHintChecked = false;
  private ccdaHintTimeoutId: any;

  constructor(
    private messagesService: UpdoxMessagesService,
    private alertService: AlertService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.message && this.message?.id) {
      this.reset();
      this.loadAttachments(true);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    clearTimeout(this.ccdaHintTimeoutId);
  }

  private reset(): void {
    this.stopPolling();
    clearTimeout(this.ccdaHintTimeoutId);
    this.attachments = [];
    this.pending = [];
    this.hasCcda = false;
    this.pollCount = 0;
    this.pollTimedOut = false;
    this.activeAddToChartId = null;
    this.isAddingToChart = false;
    this.activeRemoveFromChartId = null;
    this.isRemovingFromChart = false;
    this.activeCcdaAttachment = null;
    this.activeCcdaPendingIndex = null;
    this.ccdaHintChecked = false;
  }

  // ── Polling ────────────────────────────────────────────────────────────────

  loadAttachments(initialLoad = false): void {
    if (!this.message?.id) { return; }
    if (initialLoad) { this.isLoading = true; }

    this.messagesService.getMessageAttachments(this.message.id)
      .then(resp => {
        this.isLoading   = false;
        this.attachments = resp.attachments || [];
        this.pending     = resp.pending     || [];
        this.hasCcda     = resp.has_ccda    || false;
        this.maybeDetectCcdaPatient();

        if (this.pending.length > 0) {
          this.pollCount++;
          if (this.pollCount < MAX_POLL_ATTEMPTS) {
            this.pollTimer = setTimeout(() => this.loadAttachments(false), 4000);
          } else {
            this.pollTimedOut = true;
          }
        } else {
          this.pollCount    = 0;
          this.pollTimedOut = false;
        }
      })
      .catch(() => {
        this.isLoading = false;
        // Attachments failed to load at all — still tell the parent there's
        // nothing to check, or its "Checking for a linked patient…" state
        // would wait forever for a signal that will now never come.
        this.signalNoCcdaCandidate();
      });
  }

  retryPolling(): void {
    this.pollCount    = 0;
    this.pollTimedOut = false;
    this.loadAttachments(true);
  }

  private signalNoCcdaCandidate(): void {
    if (this.ccdaHintChecked || this.message?.patient_id) { return; }
    this.ccdaHintChecked = true;
    this.ccdaPatientDetected.emit({ matched: null, docPatient: null });
  }

  // Background, best-effort: as soon as we know the message has a C-CDA
  // attachment and no patient is linked yet, run the lightweight (patient-only)
  // parse so message-detail can offer a one-click confirm at the top — instead
  // of the user having to open Clinical Summary just to find out who the
  // document is about. Always signals a settled outcome (see ccdaPatientDetected)
  // even on a miss, error, or timeout — never just silently gives up.
  private maybeDetectCcdaPatient(): void {
    if (this.ccdaHintChecked || this.message?.patient_id) { return; }
    if (!this.hasCcda) { this.signalNoCcdaCandidate(); return; }

    // Prefer an already-processed attachment (final content) over a still-
    // pending one (parsed from raw inbound metadata, same as the C-CDA
    // preview panel does for a pending attachment).
    const processedSource = this.attachments.find(a => a.is_ccda);
    const pendingSource    = this.pending.find(p => p.is_ccda);
    if (!processedSource && !pendingSource) { this.signalNoCcdaCandidate(); return; }

    this.ccdaHintChecked = true;
    const source = processedSource
      ? { attachment_id: processedSource.id }
      : { metadata_index: pendingSource.index };
    // Pin the target: ngOnChanges swaps in a whole new message object (not a
    // mutation) when the user switches messages, so a reference check is
    // enough to detect "this response no longer belongs to what's on screen".
    const targetMessage = this.message;

    // A slow parse must not leave the parent's "Checking…" state hanging —
    // give up waiting after CCDA_HINT_TIMEOUT_MS with a "nothing (yet)"
    // signal. If the real result lands after that, it's still applied (an
    // upgrade from "nothing found" to an actual candidate) rather than
    // dropped — only the *wait* has a cap, not the lookup itself.
    let timedOut = false;
    this.ccdaHintTimeoutId = setTimeout(() => {
      timedOut = true;
      if (this.message === targetMessage && !targetMessage.patient_id) {
        this.ccdaPatientDetected.emit({ matched: null, docPatient: null });
      }
    }, CCDA_HINT_TIMEOUT_MS);

    this.messagesService.getCcdaPatientHint(targetMessage.id, source)
      .then(resp => {
        clearTimeout(this.ccdaHintTimeoutId);
        if (this.message !== targetMessage || targetMessage.patient_id) { return; }
        const best = (resp?.matched_patients || [])[0] || null;
        const docPatient = resp?.patient_in_document || null;
        if (best || !timedOut) { this.ccdaPatientDetected.emit({ matched: best, docPatient }); }
      })
      .catch(() => {
        clearTimeout(this.ccdaHintTimeoutId);
        if (this.message !== targetMessage || targetMessage.patient_id) { return; }
        if (!timedOut) { this.ccdaPatientDetected.emit({ matched: null, docPatient: null }); }
      });
  }

  private stopPolling(): void {
    if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
  }

  get totalCount(): number {
    return this.attachments.length + this.pending.length;
  }

  // ── Document viewer ────────────────────────────────────────────────────────

  viewDocument(attachment: MessageAttachment): void {
    if (attachment.document_url) { window.open(attachment.document_url, '_blank'); }
  }

  fileIcon(contentType: string): string {
    if (!contentType) { return 'fa-file-o'; }
    if (contentType.includes('pdf'))                                        { return 'fa-file-pdf-o text-danger'; }
    if (contentType.includes('xml') || contentType.includes('text/xml'))   { return 'fa-file-code-o text-info'; }
    if (contentType.includes('image'))                                      { return 'fa-file-image-o text-warning'; }
    return 'fa-file-o text-muted';
  }

  // ── C-CDA preview ──────────────────────────────────────────────────────────

  openCcdaPreview(attachment: MessageAttachment): void {
    this.activeCcdaAttachment   = attachment;
    this.activeCcdaPendingIndex = null;
    this.activeAddToChartId     = null;
  }

  openCcdaPreviewForPending(p: PendingAttachment): void {
    this.activeCcdaPendingIndex = p.index;
    this.activeCcdaAttachment   = null;
    this.activeAddToChartId     = null;
  }

  get ccdaPreviewOpen(): boolean {
    return this.activeCcdaAttachment != null || this.activeCcdaPendingIndex != null;
  }

  closeCcdaPreview(): void {
    this.activeCcdaAttachment   = null;
    this.activeCcdaPendingIndex = null;
  }

  onChartAdded(): void {
    const flagged = this.activeCcdaAttachment;
    this.closeCcdaPreview();
    this.message.added_to_medical_chart = true;
    if (flagged) {
      const att = this.attachments.find(a => a.id === flagged.id);
      if (att) { att.added_to_medical_chart = true; }
    }
    this.alertService.success('Document added to patient chart.', this.alertService.options);
  }

  // ── Add-to-chart form ──────────────────────────────────────────────────────

  // A patient must already be linked (and therefore already authorized —
  // see message-detail's sticky patient bar) before staff can file a
  // document under them; the button that calls this is disabled otherwise.
  openAddToChart(attachment: MessageAttachment): void {
    if (this.readOnly || !this.message.patient_id) { return; }
    this.activeAddToChartId     = attachment.id;
    this.activeCcdaAttachment   = null;
    this.activeCcdaPendingIndex = null;
    this.addToChartName         = this.message.subject || '';
    this.addToChartDate         = new Date().toISOString().substring(0, 10);
    this.addToChartDocTypeId    = null;

    if (!this.documentTypesLoaded) {
      this.messagesService.getDocumentTypes()
        .then(types => { this.documentTypes = types || []; this.documentTypesLoaded = true; })
        .catch(() => {});
    }
  }

  closeAddToChart(): void {
    this.activeAddToChartId = null;
  }

  submitAddToChart(): void {
    if (!this.message.patient_id) { return; } // button is disabled otherwise; guard only
    this.isAddingToChart = true;
    // Pin the target message and attachment: the component survives message
    // switches, so an in-flight save must not stamp whichever message/attachment
    // list is selected when it lands.
    const msg          = this.message;
    const attachmentId = this.activeAddToChartId;
    this.messagesService.addToChart({
      id:               msg.id,
      attachment_id:    attachmentId,
      patient_id:       msg.patient_id,
      document_type_id: this.addToChartDocTypeId  || undefined,
      name:             this.addToChartName.trim() || undefined,
      date:             this.addToChartDate        || undefined,
    }).then(() => {
      msg.added_to_medical_chart = true;
      if (this.message === msg) {
        const att = this.attachments.find(a => a.id === attachmentId);
        if (att) { att.added_to_medical_chart = true; }
        this.isAddingToChart    = false;
        this.activeAddToChartId = null;
      }
      this.alertService.success('Document added to patient chart.', this.alertService.options);
    }).catch(resp => {
      this.isAddingToChart = false;
      this.globalErrorHandler.error = resp?.error?.error || 'Failed to add document to chart.';
    });
  }

  // ── Remove-from-chart confirm ─────────────────────────────────────────────

  openRemoveFromChart(att: MessageAttachment): void {
    if (this.readOnly) { return; }
    this.activeRemoveFromChartId = att.id;
  }

  cancelRemoveFromChart(): void {
    this.activeRemoveFromChartId = null;
  }

  confirmRemoveFromChart(att: MessageAttachment): void {
    if (this.isRemovingFromChart) { return; } // guard against double-click; button isn't a real <button disabled>
    this.isRemovingFromChart = true;
    // Pin the target message: the component survives message switches, so an
    // in-flight save must not stamp whichever message is selected when it lands.
    const msg = this.message;
    this.messagesService.removeFromChart({ id: msg.id, attachment_id: att.id })
      .then(resp => {
        this.isRemovingFromChart = false;
        msg.added_to_medical_chart = resp.message_added_to_medical_chart;
        if (this.message === msg) {
          att.added_to_medical_chart = false;
          this.activeRemoveFromChartId = null;
        }
        this.alertService.success('Document removed from patient chart.', this.alertService.options);
      })
      .catch(resp => {
        this.isRemovingFromChart = false;
        this.globalErrorHandler.error = resp?.error?.error || 'Failed to remove document from chart.';
      });
  }
}
